import React, { useCallback, useContext, useMemo } from 'react';
import { useField } from 'react-final-form';
import styled from 'styled-components';
import { FlexColumn, FormModal, InputField, SelectField } from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { required } from '@ubeya/shared/utils/form';

const Container = styled(FlexColumn)`
  gap: 16px;
  padding: 16px 28px;
`;

const Hint = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.grey4};
  font-size: 13px;
  line-height: 1.4;
`;

const resolveAmountTitle = (selected, t, currencySymbol) => {
  switch (selected?.unit) {
    case 'money':
      return t('employeeLeave_moneyPot_addOrSubtract', { currency: currencySymbol || '$' });
    case 'hours':
      return t('employeeLeave_adjustAmount_hours');
    case 'days':
      return t('employeeLeave_adjustAmount_days');
    default:
      return t('addOrSubtractAmount');
  }
};

// Mirror BE zero_adjustment_amount gate.
const nonZeroNumber = (value) => {
  if (value === undefined || value === null || value === '') return 'required';
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed === 0) return 'required';
  return undefined;
};

const AmountField = ({ balances, currencySymbol }) => {
  const { t } = useContext(ConfigContext);
  const { input: leaveInput } = useField('accountContractV2LeaveId', { subscription: { value: true } });
  const selected = balances.find((b) => b.accountContractV2LeaveId === leaveInput.value);
  const title = resolveAmountTitle(selected, t, currencySymbol);
  return <InputField name="amount" title={title} type="number" validate={nonZeroNumber} required />;
};

const AdjustBalanceModal = ({
  onClose,
  adjustBalance,
  management,
  preselectedBalance,
  initialValues: initialValuesProp
}) => {
  const { t, currency } = useContext(ConfigContext);

  const balances = useMemo(() => management?.balances || [], [management]);

  const leaveOptions = useMemo(
    () =>
      balances.map((b) => ({
        label: b.leaveTypeName,
        value: b.accountContractV2LeaveId,
        unit: b.unit
      })),
    [balances]
  );

  const initialValues = useMemo(() => {
    if (initialValuesProp) return initialValuesProp;
    if (preselectedBalance?.accountContractV2LeaveId) {
      return { accountContractV2LeaveId: preselectedBalance.accountContractV2LeaveId };
    }
    return undefined;
  }, [initialValuesProp, preselectedBalance]);

  const onSubmit = useCallback(
    async (values) => {
      const selectedBalance = balances.find((b) => b.accountContractV2LeaveId === values.accountContractV2LeaveId);
      if (!selectedBalance) return;

      try {
        await adjustBalance({
          accountContractV2LeaveId: values.accountContractV2LeaveId,
          amount: Number(values.amount),
          unit: selectedBalance.unit,
          reason: values.reason || undefined
        });
        onClose();
      } catch {
        // Toast already shown by useOptimisticMutation.
      }
    },
    [adjustBalance, balances, onClose]
  );

  return (
    <FormModal
      onSubmit={onSubmit}
      initialValues={initialValues}
      size="small"
      requiredWarningFooter={t('missingRequiredFields')}
      onClose={onClose}
      title={t('adjustLeaveBalance')}
      confirmText={t('save')}
      showCancelButton
      keepDirtyOnReinitialize
      disabledOnPristine={false}>
      <Container>
        <Hint>{t('adjustLeaveBalance_hint')}</Hint>
        <SelectField
          name="accountContractV2LeaveId"
          title={t('leave')}
          options={leaveOptions}
          validate={required}
          required
          usePortal
          isMulti={false}
          clearable
        />
        <AmountField balances={balances} currencySymbol={currency} />
        <InputField name="reason" title={t('reason')} />
      </Container>
    </FormModal>
  );
};

export default AdjustBalanceModal;
